import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const PortfolioSelector = ({ selectedPortfolio, selectedDomain, onSelectPortfolio, isDrummondUser }) => {
  const [portfolios, setPortfolios] = useState([]);
  const [brands, setBrands] = useState(null); // [{domain, company_name, portfolio_count}]
  const [brandPortfolios, setBrandPortfolios] = useState({}); // domain -> portfolio[]
  const [loadingBrands, setLoadingBrands] = useState({}); // domain -> bool
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  useEffect(() => {
    loadInitialData();
  }, [isDrummondUser]);

  const loadInitialData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      let loadedPortfolios = [];

      if (isDrummondUser) {
        // Load Drummond portfolios + lightweight brand list in parallel
        const [portfolioRes, brandRes] = await Promise.all([
          api.get('/api/factsheets/portfolios'),
          api.get('/api/factsheets/portfolios/brands').catch(() => ({ data: null }))
        ]);

        loadedPortfolios = portfolioRes.data.portfolios || [];
        setPortfolios(loadedPortfolios);

        if (brandRes.data) {
          setBrands(brandRes.data.brands || []);
        }
      } else {
        // Client users get flat filtered list
        const res = await api.get('/api/factsheets/portfolios');
        loadedPortfolios = res.data.portfolios || [];
        setPortfolios(loadedPortfolios);
      }

      // Auto-select first portfolio with data if none selected
      if (!selectedPortfolio) {
        const firstValid = loadedPortfolios.find(p => p.has_portfolio_data !== false);
        if (firstValid) {
          onSelectPortfolio(firstValid.portfolio_code, null);
        }
      }
    } catch (err) {
      console.error('Error loading portfolios:', err);
      setError('Failed to load portfolios');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleGroup = async (groupKey) => {
    const isCurrentlyCollapsed = collapsedGroups[groupKey];
    const willExpand = isCurrentlyCollapsed !== false; // default state is collapsed for brands

    setCollapsedGroups(prev => ({
      ...prev,
      [groupKey]: !willExpand
    }));

    // Lazy-load brand portfolios on first expand
    if (willExpand && groupKey !== 'drummond' && !brandPortfolios[groupKey]) {
      setLoadingBrands(prev => ({ ...prev, [groupKey]: true }));
      try {
        const res = await api.get(`/api/factsheets/portfolios/brands/${encodeURIComponent(groupKey)}`);
        setBrandPortfolios(prev => ({
          ...prev,
          [groupKey]: res.data.portfolios || []
        }));
      } catch (err) {
        console.error(`Error loading portfolios for ${groupKey}:`, err);
        setBrandPortfolios(prev => ({
          ...prev,
          [groupKey]: [] // empty so it doesn't retry infinitely
        }));
      } finally {
        setLoadingBrands(prev => ({ ...prev, [groupKey]: false }));
      }
    }
  };

  const renderPortfolioItem = (portfolio, domain = null) => {
    const isActive = selectedPortfolio === portfolio.portfolio_code && selectedDomain === domain;
    const key = domain ? `${portfolio.portfolio_code}__${domain}` : portfolio.portfolio_code;
    return (
      <div
        key={key}
        className={`portfolio-item ${isActive ? 'active' : ''}`}
        onClick={() => onSelectPortfolio(portfolio.portfolio_code, domain)}
        title={portfolio.portfolio_code}
      >
        <span className="portfolio-item-name">{portfolio.name}</span>
        <span className="portfolio-item-code">{portfolio.portfolio_code}</span>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="portfolio-sidebar">
        <div className="sidebar-header">
          <h3>Portfolios</h3>
        </div>
        <div className="sidebar-loading">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="portfolio-sidebar">
        <div className="sidebar-header">
          <h3>Portfolios</h3>
        </div>
        <div className="sidebar-error">
          <i className="bi bi-exclamation-triangle me-1"></i>
          {error}
        </div>
      </div>
    );
  }

  // Drummond user with brand groups
  if (isDrummondUser && brands) {
    return (
      <div className="portfolio-sidebar">
        <div className="sidebar-header">
          <h3>Portfolios</h3>
        </div>
        <div className="sidebar-content">
          {/* Drummond portfolios — loaded upfront */}
          <div className="portfolio-group">
            <div
              className="portfolio-group-header"
              onClick={() => toggleGroup('drummond')}
            >
              <i className={`bi bi-chevron-${collapsedGroups['drummond'] === false ? 'right' : 'down'} me-1`}></i>
              DRUMMOND PORTFOLIOS
              <span className="portfolio-count">{portfolios.filter(p => p.has_portfolio_data !== false).length}</span>
            </div>
            {collapsedGroups['drummond'] !== false && (
              <div className="portfolio-group-items">
                {portfolios.filter(p => p.has_portfolio_data !== false).map(p => renderPortfolioItem(p, null))}
              </div>
            )}
          </div>

          {/* Brand groups — lazy-loaded on expand */}
          {brands.map(brand => {
            const domain = brand.domain;
            const isExpanded = collapsedGroups[domain] === false;
            const domainPortfolios = brandPortfolios[domain];
            const isLoadingDomain = loadingBrands[domain];

            return (
              <div key={domain} className="portfolio-group">
                <div
                  className="portfolio-group-header"
                  onClick={() => toggleGroup(domain)}
                >
                  <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} me-1`}></i>
                  {brand.company_name.toUpperCase()}
                  <span className="portfolio-count">{brand.portfolio_count}</span>
                </div>
                {isExpanded && (
                  <div className="portfolio-group-items">
                    {isLoadingDomain && (
                      <div className="sidebar-loading py-2">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="ms-2 small">Loading...</span>
                      </div>
                    )}
                    {domainPortfolios && domainPortfolios.filter(p => p.has_portfolio_data !== false).map(p => renderPortfolioItem(p, domain))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Client users or Drummond without brand data - flat list
  return (
    <div className="portfolio-sidebar">
      <div className="sidebar-header">
        <h3>Portfolios</h3>
      </div>
      <div className="sidebar-content">
        <div className="portfolio-group-items">
          {portfolios.filter(p => p.has_portfolio_data !== false).map(p => renderPortfolioItem(p))}
        </div>
      </div>
    </div>
  );
};

export default PortfolioSelector;
